import { AccessDeniedDetails, ApiResponse, ErrorCodes } from './_interface/apiResponse';
import { Router } from '@angular/router';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AuthorizationService } from './_services/authorization.service';
import { SessionService } from './_services/session.service';
import { POSNamePipe } from './_pipes/POSName.pipe';
import { SystemStatusService } from './_services/system-status.service';

@Injectable()
export class AccessDeniedInterceptor implements HttpInterceptor {
  constructor(private router: Router,
    private session: SessionService,
    private systemStatus: SystemStatusService,
    private injector: Injector,
    private auth: AuthorizationService) {

  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let accessDeniedDescription: string | null;


    if (!req.url.startsWith(environment.apiBase)) {
      return next.handle(req);
    }

    if (req.headers.get('SkipApiResponseInterceptor') != null) {
      const newHeaders = req.headers.delete('SkipApiResponseInterceptor');
      const newRequest = req.clone({ headers: newHeaders });
      return next.handle(newRequest);
    }

    if (req.headers.get('accessDeniedDescription') != null) {
      accessDeniedDescription = req.headers.get('accessDeniedDescription');

      const newHeaders = req.headers.delete('accessDeniedDescription');
      req = req.clone({ headers: newHeaders });
    }

    return next.handle(req).pipe(
      tap(evt => {
        if (evt instanceof HttpResponse) {
          if (evt.body && evt.body.success === true && evt.body.errorCode === undefined) {
            // a successful wrapped API call → POS & account are responding again, so auto-dismiss
            // those notices (works on ANY page, not just tables — e.g. POS reconnects while ordering).
            this.systemStatus.clear('pos');
            this.systemStatus.clear('subscription');
          }
          if (evt.body && evt.body.success !== undefined && !evt.body.success && evt.body.errorCode) {
            let message: string = '';
            let infoBarKey: string | null = null;

            switch (evt.body.errorCode as ErrorCodes) {
              case (ErrorCodes.ProductNotFound):
                message = 'Proizvod nije nađen';
                break;
              case (ErrorCodes.AccessDenied):
                message = this.accessDeniedMessage(evt.body, accessDeniedDescription);
                break;
              case (ErrorCodes.InvalidCompany):
                message = 'Kompanija nije nađena';
                break;
              case (ErrorCodes.SubscriptionExpired):
                message = 'Pretplata je istekla';
                infoBarKey = 'subscription';
                break;
              case (ErrorCodes.InvalidOperation):
                message = 'Akcija nije moguća';
                break;
              case (ErrorCodes.MailAlreadyRegistered):
                message = 'eMail adresa je već registrovana';
                break;
              case (ErrorCodes.InvalidToken): {
                message = 'Sesija istekla. Prijavite se opet';
                this.router.navigateByUrl('/logout');
                break;
              }
              case (ErrorCodes.UserDisabled): {
                message = 'Nalog zaključan';
                this.router.navigateByUrl('/logout');
                break;
              }
              case (ErrorCodes.AlreadyExtended): {
                message = 'Period je već produžen';
                break;
              }
              case (ErrorCodes.FileSizeTooBig): {
                message = 'Neodgovarajuća veličina fajla';
                break;
              }
              case (ErrorCodes.QRCodeTaken): {
                message = 'QR kod je već dodeljen';
                break;
              }
              case (ErrorCodes.TooManyGlobalQRCodes): {
                message = 'Previše QR kodova koji ne počinju sa "Q-"';
                break;
              }
              case (ErrorCodes.InvalidIPAddress): {
                message = 'Operacija nije dozvoljena sa vaše IP adrese';
                break;
              }
              case (ErrorCodes.InvalidSession): {
                this.auth.signOut().then(
                  () => this.router.navigate(['/login'], { state: { invalidSession: true } })
                );

                break;
              }
              case (ErrorCodes.NoResponseFromPOS): {
                var pp = new POSNamePipe();
                var posName = pp.transform(this.session.selectedCompany.value?.posName);
                message = posName + " nije odgovorio. Proverite komunikaciju programa sa eMeni sistemom";
                infoBarKey = 'pos';
                break;
              }
              case (ErrorCodes.POSNotConnected): {
                var pp = new POSNamePipe();
                var posName = pp.transform(this.session.selectedCompany.value?.posName);
                message = posName + " nije povezan. Proverite komunikaciju programa sa eMeni sistemom";
                infoBarKey = 'pos';
                break;
              }
              case (ErrorCodes.InvalidCode): {
                message = "Kod nije ispravan ili je već istekao";
                break;
              }
              default:
                message = 'Greška: ' + evt.body.errorCode + ' - ' + evt.body.errorDescription;
                break;
            }
            if (message) {
              if (infoBarKey) {
                // Persistent backend status (POS down, subscription expired…) repeats on every
                // call → show ONE info-bar notice below the header instead of a toast every few
                // seconds. Auto-dismissed on the next success ('pos' in tables.component on a good
                // summary load; 'subscription' below on any successful API response).
                this.systemStatus.set(infoBarKey, 'error', message);
              } else {
                this.systemStatus.set('apiError', 'error', message, false, 6000);
              }
            }
          }
        }
      })
    );
  }

  /**
   * „Nemate prava za ovu akciju" ne kaže korisniku šta da traži od menadžera, a dosta poziva ide
   * automatski (bez klika), pa se poruka pojavi bez ikakvog konteksta. Server zato uz AccessDenied
   * šalje `details.requiredPermission` (v. `AccessDeniedDetails`), a opis same akcije i dalje nosi
   * `accessDeniedDescription` zaglavlje zahteva — poruka koristi šta god ima:
   * „Nemate prava da vidite račune — potrebno pravo: Pregled faktura".
   */
  private accessDeniedMessage(body: ApiResponse, rawAction: string | null): string {
    // TranslateService se dovlači lenjo: njegov loader ide preko HttpClient-a, pa bi ga
    // konstruktor interceptora vukao u krug (NG0200).
    const translate = this.injector.get(TranslateService);
    const permission = this.requiredPermissionName(body, translate);
    // Opis akcije je i18n ključ (`accessDeniedAction.*`) ili — kod starijih poziva — gotov tekst.
    // Ključ se prepoznaje po tome što je jedna reč sa tačkom; gotov opis ima razmake, pa se ne
    // provlači kroz `instant` (MissingTranslationHandler bi ga prijavio u konzoli kao nedostajući).
    const isKey = !!rawAction && !/\s/.test(rawAction) && rawAction.includes('.');
    const action = isKey ? (this.translated(rawAction!, translate) ?? rawAction) : rawAction;

    if (permission && action) {
      return translate.instant('noPermission.actionPermission', { action, permission });
    }

    if (permission) {
      return translate.instant('noPermission.permission', { permission });
    }

    if (action) {
      return translate.instant('noPermission.action', { action });
    }

    return translate.instant('noPermission.generic');
  }

  /**
   * Prevedeni naziv traženog prava, ili null kad odbijanje ne dolazi iz kataloga prava
   * (nije zaposlen u lokalu, tuđi QR kod…) — tada poruka ostaje bez naziva prava.
   */
  private requiredPermissionName(body: ApiResponse, translate: TranslateService): string | null {
    // Fallback dok se novi backend ne raširi: naziv prava stoji i u tekstu greške
    // („ModifyOrder denied for userID…" / „Requested permission: ModifyOrder for userID…").
    const code = (body?.details as AccessDeniedDetails)?.requiredPermission
      || body?.errorDescription?.match(/^(?:Requested permission: )?([A-Za-z]+) (?:denied|for userID)/)?.[1];

    if (!code) {
      return null;
    }

    // Pravo dodato na serveru a još neprevedeno — sirov naziv je i dalje bolji od ključa.
    return this.translated('permission.' + code, translate) ?? code;
  }

  /** Prevod, ili null kad ključ nije preveden (`instant` tada vraća sam ključ). */
  private translated(key: string, translate: TranslateService): string | null {
    const label = translate.instant(key);
    return label === key ? null : label;
  }
}
